import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Box,
  CircularProgress,
  Card,
  Paper,
  CardMedia,
  Grid,
} from "@mui/material";
import { styled } from "@mui/system";
import styles from "./ViewBooking.module.css";
import { useUserContext } from "../../../Context/UserContext";
import { useToastContext } from "../../../Context/ToastContext";
import { Helmet } from "react-helmet";
import { apiGet } from "../../../utils/ApiManager";

const fetchBooking = process.env.REACT_APP_FETCH_BOOKING_DETAILS;

// Custom styled Paper for sections (Updated)
const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  background:
    "linear-gradient(to right, rgb(229, 240, 233), rgb(248, 253, 248))", // Gradient background
  borderRadius: "12px",
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)", // Subtle shadow
  color: "#333",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 6px 15px rgba(0, 0, 0, 0.3)",
  },
}));

const DetailText = styled(Typography)(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 500,
  marginTop: theme.spacing(1),
  color: "#555",
}));

const ViewBooking = () => {
  const { bookingId } = useParams();
  const { showToast } = useToastContext();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response  = await apiGet(`${fetchBooking}/${bookingId}`);
      if (response?.success) {
        setBooking(response?.data || null);
      } else {
        showToast(
          response?.message || "Failed to fetch booking details !",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
        error?.message ||
        "Failed to fetch booking details !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  if (loading) {
    return <CircularProgress />;
  }

  if (!booking) {
    return <Typography>No booking details found.</Typography>;
  }

  return (
    <>
      <Helmet>
        <title>Bookings | DiNEST</title>
        <meta name="description" content="View bookings on DiNEST" />
      </Helmet>

      <Box className={styles.container}>
        <Typography variant="h4" className={styles.containerTitle}>
          Bookings Details
        </Typography>

        {/* Restaurant Overview */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Your Booking</h2>
          <DetailText>
            <strong>Booking ID:</strong> {booking.bookingId}
          </DetailText>
          <DetailText>
            <strong>Status:</strong> {booking.bookingStatus.toUpperCase()}
          </DetailText>
          <DetailText>
            <strong>Date:</strong>{" "}
            {new Date(booking.bookingDate).toLocaleDateString("en-GB")}
          </DetailText>
          <DetailText>
            <strong>Time:</strong> {booking.startTime} - {booking.endTime}
          </DetailText>
          <DetailText>
            <strong>People:</strong> {booking.peopleCount} pax
          </DetailText>
          <DetailText>
            <strong>Total Amount:</strong> ₹{booking.subTotalAmount ? (booking.subTotalAmount - (booking?.discountAmount || 0)) +( booking?.gstAmount || 0)  : 0 }/-
          </DetailText>
           {["rejected","cancelled"].includes(booking?.bookingStatus) && <DetailText>
            <strong>Is Refunded :</strong>  {booking?.isRefunded ? "Yes" :"No"}
          </DetailText>}
        </SectionPaper>

        {/* Restaurant Info */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Restaurant Info</h2>
          <DetailText>
            <strong>Name:</strong> {booking.restaurant?.restaurantName}
          </DetailText>
          <DetailText>
            <strong>Email:</strong> {booking.restaurant?.restaurantEmail}
          </DetailText>
          <DetailText>
            <strong>Phone:</strong> {booking.restaurant?.restaurantNumber}
          </DetailText>
        </SectionPaper>

        {/* {booking?.payments?.length > 0 && ( */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Payments</h2>
          {booking?.payments?.length > 0 ? (
            booking?.payments?.map((payment, index) => {
              return (
                // <Card className={styles.paymentCard} key={index}>
                <>
                  <DetailText>
                    <strong>Payment Type:</strong>{" "}
                    {payment?.isPartial ? "Partial Payment" : "Final Payment"}
                  </DetailText>
                  <DetailText>
                    <strong>OrderId:</strong> {payment?.orderId}
                  </DetailText>
                  <DetailText>
                    <strong>PaymenId:</strong> {payment?.paymentId}
                  </DetailText>
                  <DetailText>
                    <strong>Status:</strong>{" "}
                    {String(payment?.status).toUpperCase()}
                  </DetailText>
                  <DetailText>
                    <strong>Amount:</strong> ₹{payment?.amount}/-
                  </DetailText>
                </>
                // </Card>
              );
            })
          ) : (
            <h3 style={{ marginTop: "2rem" }}>No Payments Found !</h3>
          )}
        </SectionPaper>

        {/* )} */}
        {/* Booked By */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Booked By</h2>
          <DetailText>
            <strong>Name:</strong> {booking.bookedBy?.firstName}{" "}
            {booking.bookedBy?.lastName}
          </DetailText>
          <DetailText>
            <strong>User ID:</strong> {booking.bookedBy?.userId}
          </DetailText>
          <DetailText>
            <strong>Email:</strong> {booking.bookedBy?.email}
          </DetailText>
          <DetailText>
            <strong>Phone:</strong> {booking.bookedBy?.phoneNo || "N/A"}
          </DetailText>
          {booking.bookedBy?.profileImage && (
            <img
              src={booking.bookedBy.profileImage}
              alt="Profile"
              width="120"
              height="120"
              style={{ borderRadius: "50%", marginTop: "10px" }}
            />
          )}
        </SectionPaper>

        {/* Menu Ordered */}
        <SectionPaper className={styles.sectionColor}>
          <h2>Menu Ordered</h2>

          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: {
                xs: "center",
                sm: "flex-start",
              },
            }}
            alignItems="stretch"
            marginTop={2}
            marginBottom={2}
          >
            {booking?.menu?.map((item, index) => (
              <Grid key={index}>
                <Card
                  elevation={3}
                  className={styles.menuCard}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {/* Image Carousel */}
                  <Box sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}>
                    {item?.dishData.images?.length > 0 && (
                      <DishImageCarousel images={item.dishData.images} />
                    )}
                  </Box>

                  {/* Details */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                      padding: 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "bold", color: "#207d46", mb: 1 }}
                    >
                      {item.dishData.title || "Dish Name"}
                    </Typography>

                    <Typography variant="subtitle2">
                      <strong>Price:</strong> ₹{item.price}/-
                    </Typography>
                    <Typography variant="subtitle2">
                      <strong>Qty:</strong> {item.qty}
                    </Typography>
                    <Typography variant="subtitle2">
                      <strong>Total:</strong> ₹{item.price * item.qty}/-
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </SectionPaper>
      </Box>
    </>
  );
};

// Dish Image Carousel Component
const DishImageCarousel = ({ images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      nextImage();
    }, 3000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        borderTopRightRadius: 2,
        borderTopLeftRadius: 2,
        overflow: "hidden",
        width: "100%",
        height: 160,
        boxShadow: 1,
      }}
    >
      <CardMedia
        component="img"
        image={images[currentIndex]}
        alt={`Dish image ${currentIndex + 1}`}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />

      {/* Arrow Buttons */}
      {/* <IconButton
        sx={{
          position: "absolute",
          left: 5,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          backgroundColor: "rgba(255,255,255,0.7)",
          "&:hover": { color: 'white', backgroundColor: "#207d46" },
        }}
        onClick={prevImage}
      >
        <KeyboardArrowLeftIcon />
      </IconButton> */}

      {/* <IconButton
        sx={{
          position: "absolute",
          right: 5,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          backgroundColor: "rgba(255,255,255,0.7)",
          "&:hover": { color: 'white', backgroundColor: "#207d46" },
        }}
        onClick={nextImage}
      >
        <KeyboardArrowRightIcon />
      </IconButton> */}

      {/* Dots */}
      <Box
        sx={{
          position: "absolute",
          bottom: 5,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 1,
        }}
      >
        {images.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: currentIndex === index ? "#207d46" : "#ccc",
              cursor: "pointer",
            }}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ViewBooking;
